/**
 * Talking to the Operations API.
 *
 * Three rules this module exists to keep:
 *
 *   1. `credentials: 'include'` everywhere — the session is an opaque host-only cookie the
 *      browser holds and this code cannot read.
 *   2. A 503 is NOT a 403. The API distinguishes "we cannot tell right now" from "you may
 *      not", and the UI must too: one is a disconnected state, the other is a denial.
 *   3. No demo fallback. When a call fails, the caller gets a failure — never a plausible
 *      number. §2 forbids silently substituting demo values, and a fallback here is exactly
 *      how that happens.
 */

export const BASE = import.meta.env['VITE_OPS_API'] ?? '';

export type ApiFailure =
  | { kind: 'unauthenticated' }        // 401 — no session, or it expired
  | { kind: 'forbidden'; reason: string }  // 403 — denied, and we can say why
  | { kind: 'unavailable'; reason: string }  // 503 — identity provider or source unreachable
  | { kind: 'conflict'; current: unknown }   // 409 — stale write
  | { kind: 'error'; reason: string };

export type ApiResult<T> = { ok: true; data: T } | { ok: false; failure: ApiFailure };

/** The API sets __Host-ops_csrf on its own host, which this page (another host) cannot read, so the
 *  token comes from /api/v1/me and is held in memory. Same double-submit check on the server. */
let csrf = '';
async function csrfToken(): Promise<string> {
  if (!csrf) {
    try {
      const r = await fetch(`${BASE}/api/v1/me`, { credentials: 'include' });
      if (r.ok) csrf = ((await r.json()) as { csrfToken?: string | null }).csrfToken ?? '';
    } catch { /* the request below reports the failure */ }
  }
  return csrf;
}

export async function call<T>(
  path: string,
  init: RequestInit = {},
): Promise<ApiResult<T>> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  if (method !== 'GET' && method !== 'HEAD') {
    headers.set('Content-Type', 'application/json');
    headers.set('X-Ops-CSRF', await csrfToken());
  }

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...init, method, headers, credentials: 'include' });
  } catch {
    // Network failure. Unreachable is not permission, and it is not zero.
    return { ok: false, failure: { kind: 'unavailable', reason: 'The Operations API is unreachable.' } };
  }

  if (res.status === 401) return { ok: false, failure: { kind: 'unauthenticated' } };

  if (res.status === 503 || res.status === 403 || res.status === 409 || !res.ok) {
    let body: Record<string, unknown> = {};
    try { body = (await res.json()) as Record<string, unknown>; } catch { /* keep {} */ }
    // Prefer the server's own sentence when it sends one. `error` is a machine code; `reason`
    // is written for a person and is almost always more useful than explain()'s fallback.
    const detail = typeof body['reason'] === 'string' ? body['reason'] : null;
    const reason = detail ?? String(body['error'] ?? res.statusText);

    if (res.status === 503) return { ok: false, failure: { kind: 'unavailable', reason: detail ?? explain(reason) } };
    if (res.status === 403 && reason.startsWith('csrf_')) csrf = ''; // stale after a new sign-in: refetch next time
    if (res.status === 403) return { ok: false, failure: { kind: 'forbidden', reason: explain(reason) } };
    if (res.status === 409) return { ok: false, failure: { kind: 'conflict', current: body['current'] } };
    return { ok: false, failure: { kind: 'error', reason: explain(reason) } };
  }

  if (res.status === 204) return { ok: true, data: undefined as T };
  return { ok: true, data: (await res.json()) as T };
}

/** Turn an API error code into something a person can act on. */
function explain(code: string): string {
  switch (code) {
    case 'stale_role_unverifiable':
    case 'identity_provider_unavailable':
      return 'PeerTube cannot be reached, so your administrator role cannot be confirmed. ' +
        'Access is withheld until it can be — this is deliberate, not a fault in your account.';
    case 'not_on_allowlist':
      return 'Your PeerTube account is an administrator, but it has not been granted ' +
        'Operations Center access. Ask an existing operator to add you.';
    case 'not_admin':
      return 'This account is not a PeerTube administrator.';
    case 'session_expired':
      return 'Your session has expired. Sign in again.';
    case 'csrf_token_missing':
    case 'csrf_token_mismatch':
    case 'origin_mismatch':
      return 'That request could not be verified as coming from this page. Reload and retry.';
    default:
      return code;
  }
}

// ------------------------------------------------------------------- types

export interface Preferences {
  mentionsEnabled: boolean;
  severities: string[];
  services: string[];
  quietHoursEnabled: boolean;
  quietStart: string;
  quietEnd: string;
  timeZone: string;
  criticalOverridesQuietHours: boolean;
  recoveryMentions: boolean;
  discordLinked: boolean;
  discordUsername: string | null;
  version: number;
  updatedAt: string;
}

export const getPreferences = () =>
  call<Preferences>('/api/v1/me/notification-preferences');

export const savePreferences = (patch: Partial<Preferences> & { version: number }) =>
  call<Preferences>('/api/v1/me/notification-preferences', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });

export const unlinkDiscord = () =>
  call<void>('/api/v1/me/discord-link', { method: 'DELETE' });

// ------------------------------------------------------------------ metrics

export interface MetricSample { labels: Record<string, string>; value: number }
export interface MetricResult {
  id: string;
  unit: 'count' | 'ratio' | 'bytes' | 'seconds' | 'cores' | 'usd';
  source: string;
  description: string;
  observedAt: string;
  stale: boolean;
  samples: MetricSample[];
  truncated?: number;
}

/** The browser names a query. It cannot write one — the catalog lives on the server. */
export const getMetric = (id: string) => call<MetricResult>(`/api/v1/metrics/${encodeURIComponent(id)}`);

export function formatValue(v: number, unit: MetricResult['unit']): { value: string; unit?: string } {
  switch (unit) {
    case 'ratio':   return { value: (v * 100).toFixed(1), unit: '%' };
    case 'bytes':   return formatBytes(v);
    case 'seconds':
      // Backup ages and certificate expiries run to days; minutes would read as 51,840 min.
      if (Math.abs(v) >= 2 * 86400) return { value: (v / 86400).toFixed(1), unit: 'days' };
      if (Math.abs(v) >= 2 * 3600) return { value: (v / 3600).toFixed(1), unit: 'h' };
      return v >= 60 ? { value: (v / 60).toFixed(1), unit: 'min' } : { value: v.toFixed(1), unit: 's' };
    case 'cores':   return { value: v.toFixed(2), unit: 'cores' };
    case 'usd':     return { value: `$${Math.round(v).toLocaleString('en-US')}`, unit: '/month' };
    default:
      // Counts are discrete. Prometheus increase() extrapolates and returns a fraction, so a
      // restart count arrives as 7.002 — rendering that implies a precision the metric does
      // not have, and reads like a bug.
      return { value: Math.round(v).toLocaleString('en-US') };
  }
}

function formatBytes(v: number): { value: string; unit: string } {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, n = v;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return { value: n.toFixed(n >= 100 ? 0 : 1), unit: units[i]! };
}

// ------------------------------------------------------------------- alerts

export interface FiringAlert {
  alertname: string;
  severity: string;
  service: string | null;
  environment: string | null;
  summary: string | null;
  provisional: boolean;
  activeSince: string | null;
  runbookUrl: string | null;
}

export interface AlertsSnapshot {
  rules: { loaded: number; provisional: number; unhealthy: number; unhealthyNames: string[] };
  firing: FiringAlert[];
  /** Always true when healthy. FALSE means the alerting path itself is broken. */
  watchdogFiring: boolean;
  incidents: { open: number; acknowledged: number };
  observedAt: string;
}

export const getAlerts = () => call<AlertsSnapshot>('/api/v1/alerts');

// ------------------------------------------------------------------ console

export interface DeliveryRow {
  id: string;
  summary: string;
  severity: string | null;
  state: string;
  attempts: number;
  mentioned: number;
  lastError: string | null;
  createdAt: string;
  lastAttempt: { at: string; httpStatus: number | null; outcome: string } | null;
}

export interface RoutingPerson {
  peertubeUserId: number;
  username: string | null;
  authorized: boolean;
  discordLinked: boolean;
  discordUsername: string | null;
  mentionsEnabled: boolean;
  reachable: boolean;
  blockedBy: string | null;
}

export interface RoutingSnapshot {
  people: RoutingPerson[];
  reachable: number;
  channel: string;
  observedAt: string;
}

export interface IntegrationStatus {
  name: string;
  purpose: string;
  state: 'ok' | 'down' | 'unknown';
  detail: string;
}

export const getDelivery = () => call<{ deliveries: DeliveryRow[] }>('/api/v1/delivery?limit=50');
export const getRouting = () => call<RoutingSnapshot>('/api/v1/routing');
export const getIntegrations = () =>
  call<{ integrations: IntegrationStatus[]; observedAt: string }>('/api/v1/integrations');

export interface Me {
  peertubeUserId: number;
  username: string | null;
  isAdmin: boolean | null;
  roleObservedAt: string | null;
  lastRoleCheck: string;
  sessionExpiresAt: string;
  discordLinked: boolean;
  discordUsername: string | null;
}

export const getMe = () => call<Me>('/api/v1/me');

/* ------------------------------------------------------------ load-test lab */

export interface Binding { planId: string; planSha256: string; commit: string; imageDigest: string; ceilingsVersion: string }
interface PlanCommon { purpose: string; target: { env: string; hosts: string[]; port: number; paths: string[] }; duration_max: string;
  window_utc: [string, string]; budget_bytes: number }
export interface ApiPlanView extends PlanCommon { suite: 'api' | 'player';
  workload: { stages: { duration: string; rps: number }[]; max_vus: number }; thresholds: { p95_ms: number; error_rate: number };
  abort: { p95_vs_baseline: number; error_rate: number; peertube_cpu: number } }
export interface MediaPlanView extends PlanCommon { suite: 'media';
  workload: { stages: { duration: string; viewers: number }[]; max_vus: number; bitrate_bps: number };
  thresholds: { error_rate: number; segment_error_rate: number; start_p95_s: number; rebuffer_ratio: number; stalled_segment_rate: number; session_failure_rate: number };
  abort: { p95_vs_baseline: number; error_rate: number; peertube_cpu: number; segment_p95_s: number } }
export interface TestPlanView { id: string; plan: ApiPlanView | MediaPlanView;
  current: Binding; approval: (Binding & { by: number; at: string }) | null; approvalValid: boolean; mismatch: string[];
  estimate: { requests: number; bytes: number; costUsd: number; assumption: string }; maxTrafficAfterStop: number }
/** k6.video of a media run's newest sample. segmentFailed, sessionOk and stalledSegments are rates (0..1); the rest are counts or seconds. */
export interface VideoSample { viewers?: number; startP95S?: number; segmentP95S?: number; segmentFailed?: number; sessionOk?: number;
  rebufferAvg?: number; stalledSegments?: number; stalls?: number; mediaBytes?: number; segments?: number; slowSegments?: number }
export interface TestRunView { id: number; planId: string; status: string; cancelRequested: boolean; stopConfirmedAt: string | null;
  abortReason: string | null; generatorLimited: boolean | null; verified: boolean | null; startedAt: string | null; endedAt: string | null;
  createdAt: string; evidenceKey: string | null; video: VideoSample | null; shardsClaimed: number; shardsTotal: number }
export const getTestPlans = () => call<{ plans: TestPlanView[]; canApprove: boolean; rejected: { file: string; errors: string[] }[] }>('/api/v1/tests/plans');
export interface CapacityRunView { id: number; planId: string; env: string; mix: string; status: string; abortReason: string | null;
  verified: boolean | null; startedAt: string; heldRps: number | null; maxPlannedRps: number; steps: { rps: number; held: boolean }[] }
export const getCapacity = () => call<{ runs: CapacityRunView[] }>('/api/v1/tests/capacity');
export const getTestRuns = () => call<{ runs: TestRunView[] }>('/api/v1/tests/runs');
export const approvePlan = (id: string, binding: Binding) => call<{ ok: true }>(`/api/v1/tests/plans/${encodeURIComponent(id)}/approve`, { method: 'POST', body: JSON.stringify({ binding }) });
export const startRun = (id: string) => call<{ runId: number }>(`/api/v1/tests/plans/${encodeURIComponent(id)}/runs`, { method: 'POST' });
export const cancelRun = (id: number) => call<{ ok: true }>(`/api/v1/tests/runs/${id}/cancel`, { method: 'POST' });
